import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {
  EditorArrowTool,
  EditorMeasureTool,
  EditorSelectionPanel,
  EditorShapeTool,
  EditorTextSizeTool,
  EditorTool,
  EditorToolColor,
  EditorToolWidth
} from "../../../models/editor-tools";
import {animate, style, transition, trigger} from "@angular/animations";
import {Utils} from "../../../utils";

const enterTransition = transition(':enter', [
  style({
    opacity: 0
  }),
  animate('.2s ease-in', style({
    opacity: 1
  }))
]);

const leaveTrans = transition(':leave', [
  style({
    opacity: 1
  }),
  animate('.2s ease-out', style({
    opacity: 0
  }))
])

const fadeIn = trigger('fadeIn', [
  enterTransition
]);

const fadeOut = trigger('fadeOut', [
  leaveTrans
]);


export interface EditorToolsStatus {
  selectedTool: EditorTool;
  selectedSubtool: EditorArrowTool | EditorShapeTool | EditorMeasureTool | EditorTextSizeTool;
  selectedColor: EditorToolColor;
  selectedWidth: EditorToolWidth;
}


@Component({
  selector: 'app-editor-tools',
  templateUrl: './editor-tools.component.html',
  styleUrls: ['./editor-tools.component.scss'],
  animations: [
    fadeIn,
    fadeOut
  ]
})
export class EditorToolsComponent implements OnInit {

  @Input()
  tool: EditorTool;

  selectedTool: EditorTool;
  selectedSubtool: EditorArrowTool | EditorShapeTool | EditorMeasureTool | EditorTextSizeTool;
  selectedColor: EditorToolColor;
  selectedWidth: EditorToolWidth;

  @ViewChild('selectionPanel', { static: false })
  selectionPanel: ElementRef;
  selectionPanelType: EditorSelectionPanel;
  showSelectionPanel = false;
  selectionPanelLeftOffset: number;

  @ViewChild('subtoolElem', { static: false })
  subtoolElem: ElementRef
  @ViewChild('colorElem', { static: false })
  colorElem: ElementRef
  @ViewChild('widthElem', { static: false })
  widthElem: ElementRef

  @Output()
  onEditorUpdate = new EventEmitter<EditorToolsStatus>();

  constructor() { }

  ngOnInit(): void {
    this.init();
  }

  init() {
    this.setTool(this.tool ?? EditorTool.ARROW, true);
    if (this.selectedTool == EditorTool.ARROW) {
      this.setSubtool(EditorArrowTool.ARROW_LEFT, true);
    } else if (this.selectedTool == EditorTool.SHAPE) {
      this.setSubtool(EditorShapeTool.SHAPE_CIRCLE, true);
    } else if (this.selectedTool == EditorTool.MEASURE) {
      this.setSubtool(EditorMeasureTool.MEASURE_LINE, true);
    } else if (this.selectedTool == EditorTool.TEXT) {
      this.setSubtool(EditorTextSizeTool.TEXT_MEDIUM, true);
    }
    this.setColor(EditorToolColor.RED, true);
    this.setWidth(EditorToolWidth.REGULAR, true);

    // Send first update
    this.sendEditorUpdate();
  }

  sendEditorUpdate() {
    this.onEditorUpdate.emit({
      selectedTool: this.selectedTool,
      selectedSubtool: this.selectedSubtool,
      selectedColor: this.selectedColor,
      selectedWidth: this.selectedWidth
    });
  }


  /**
   * Tool selection methods
   **/

  getToolLabel() {
    switch (this.selectedTool) {
      case EditorTool.ARROW:
        return 'Arrow';
      case EditorTool.SHAPE:
        return 'Shape';
      case EditorTool.MEASURE:
        return 'Measure';
      case EditorTool.FREEHAND:
        return 'Freehand';
      case EditorTool.TEXT:
        return 'Text';
    }
    return 'Tool';
  }

  setTool(tool: EditorTool, skipUpdate?: boolean) {
    this.selectedTool = tool;
    if (!skipUpdate) {
      this.sendEditorUpdate();
    }
  }


  /**
   * Subtool selection methods
   **/

  setSubtool(subtool: EditorArrowTool | EditorShapeTool | EditorMeasureTool | EditorTextSizeTool, skipUpdate?: boolean) {
    this.selectedSubtool = subtool;
    if (!skipUpdate) {
      this.sendEditorUpdate();
    }
  }

  getSubtoolIcon(tool?: EditorTool, subtool?: EditorArrowTool | EditorShapeTool | EditorMeasureTool | EditorTextSizeTool) {

    tool = tool ?? this.selectedTool;
    subtool = subtool ?? this.selectedSubtool;

    if (tool == EditorTool.ARROW) {
      switch (subtool as EditorArrowTool) {
        case EditorArrowTool.ARROW_LEFT:
          return 'west';
        case EditorArrowTool.ARROW_UP:
          return 'north';
        case EditorArrowTool.ARROW_RIGHT:
          return 'east';
        case EditorArrowTool.ARROW_BOTTOM:
          return 'south';
        case EditorArrowTool.ARROW_CLOCKWISE:
          return 'rotate_right';
        case EditorArrowTool.ARROW_ANTICLOCKWISE:
          return 'rotate_left';
      }
    } else if (tool == EditorTool.SHAPE) {
      switch (subtool as EditorShapeTool) {
        case EditorShapeTool.SHAPE_CIRCLE:
          return 'radio_button_unchecked';
        case EditorShapeTool.SHAPE_RECTANGLE:
          return 'crop_square';
        case EditorShapeTool.SHAPE_LINE:
          return 'horizontal_rule';
      }
    } else if (tool == EditorTool.MEASURE) {
      switch (subtool as EditorMeasureTool) {
        case EditorMeasureTool.MEASURE_CIRCLE:
          return 'radio_button_unchecked';
        case EditorMeasureTool.MEASURE_RECTANGLE:
          return 'crop_square';
        case EditorMeasureTool.MEASURE_LINE:
          return 'horizontal_rule';
      }
    } else if (tool == EditorTool.TEXT) {
      return 'title';
    }
    return '';
  }

  getSubtoolIconSize(tool?: EditorTool, subtool?: EditorArrowTool | EditorShapeTool | EditorMeasureTool | EditorTextSizeTool) {

    tool = tool ?? this.selectedTool;
    subtool = subtool ?? this.selectedSubtool;

    if (tool != EditorTool.TEXT) {
      return '';
    }

    return (12 + subtool * 6) + 'px';
  }

  getAllSubtools() {
    switch (this.selectedTool) {
      case EditorTool.ARROW:
        return Utils.enumToArray(EditorArrowTool);
      case EditorTool.SHAPE:
        return Utils.enumToArray(EditorShapeTool);
      case EditorTool.MEASURE:
        return Utils.enumToArray(EditorMeasureTool);
      case EditorTool.TEXT:
        return Utils.enumToArray(EditorTextSizeTool);
    }
  }

  openSubtoolSelectionDialog() {
    this.selectionPanelType = EditorSelectionPanel.SUBTOOL;
    this.showSelectionPanel = true;
    this.selectionPanelLeftOffset = this.subtoolElem.nativeElement.offsetLeft + this.subtoolElem.nativeElement.clientWidth / 2 - 115;
  }

  isCurrentSubtool(subtool: EditorArrowTool | EditorShapeTool | EditorMeasureTool) {
    return subtool == this.selectedSubtool;
  }


  /**
   * Width selection methods
   **/

  setWidth(width: EditorToolWidth, skipUpdate?: boolean) {
    this.selectedWidth = width;
    if (!skipUpdate) {
      this.sendEditorUpdate();
    }
  }

  getWidth(width?: number) {
    return (((width ?? this.selectedWidth) + 1) * 2) + 'px';
  }

  openWidthSelectionDialog() {
    this.selectionPanelType = EditorSelectionPanel.WIDTH;
    this.showSelectionPanel = true;
    this.selectionPanelLeftOffset = this.widthElem.nativeElement.offsetLeft + this.widthElem.nativeElement.clientWidth / 2 - 115;
  }

  getAllWidths() {
    return Utils.enumToArray(EditorToolWidth);
  }

  isCurrentWidth(width: EditorToolWidth) {
    return width == this.selectedWidth;
  }


  /**
   * Color selection methods
   **/

  setColor(color: EditorToolColor, skipUpdate?: boolean) {
    this.selectedColor = color;
    if (!skipUpdate) {
      this.sendEditorUpdate();
    }
  }

  getColor() {
    return this.selectedColor;
  }

  openColorSelectionDialog() {
    this.selectionPanelType = EditorSelectionPanel.COLOR;
    this.showSelectionPanel = true;
    this.selectionPanelLeftOffset = this.colorElem.nativeElement.offsetLeft + this.colorElem.nativeElement.clientWidth / 2 - 115;
  }

  getAllColors() {
    return Object.values(EditorToolColor);
  }

  isCurrentColor(color: EditorToolColor | string) {
    return color == this.selectedColor;
  }


  /**
   * General methods
   **/

  closeSelectionDialog() {
    this.showSelectionPanel = false;
  }

  get EditorTool() { return EditorTool; }
  get EditorSelectionPanel() { return EditorSelectionPanel; }
  get EditorToolColor() { return EditorToolColor; }

}
